import React from "react";
import { useDiscoverContext } from "../context/discover_context";
import { 
  Heart, 
  Search, 
  ArrowRight, 
  Filter, 
  Star, 
  Sparkles, 
  Lock, 
  Users, 
  MessageCircle, 
  Calendar, 
  Clock, 
  X,
  ChevronDown 
} from 'lucide-react';
import { useDispatch } from "react-redux";
import { menuActions } from "../../../di/di";
import { FiMenu } from 'react-icons/fi';
const DiscoverComponent = () => {
    // Use the context instead of local state
    const dispatch = useDispatch();
    const {
        // States
        selectedCategory,
        selectedAgeRange,
        selectedPersonality,
        searchQuery,
        hoveredAvatar,
        showFilters,
        featuredAvatar,
        detailsModal,
        // Setters
        setSelectedCategory,
        setSelectedAgeRange,
        setSelectedPersonality,
        setSearchQuery,
        setHoveredAvatar,
        setShowFilters,
        setFeaturedAvatar,
        setDetailsModal,
        // Data
        categories,
        ageRanges,
        personalities,
        avatars,
        filteredAvatars,
        // Styles
        animationStyles
    } = useDiscoverContext();

    const toggleDrawer = () => {
        dispatch(menuActions.toggleMenu());
    };

    // Modal component for avatar details
    const AvatarDetailsModal = ({ avatar, onClose }) => {
        if (!avatar) return null;
        
        return (
            <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
                <div 
                    className="bg-gradient-to-b from-slate-950/95 to-fuchsia-950/90 border border-red-900/30 rounded-xl w-full max-w-3xl max-h-[85vh] overflow-auto shadow-2xl animate-fade-in" 
                    onClick={e => e.stopPropagation()}
                >
                    <div className="relative p-6">
                        <button 
                            className="text-base absolute top-4 right-4 p-1 rounded-full bg-red-900/30 hover:bg-red-900/50 transition-colors" 
                            onClick={onClose}
                        >
                            <X size={20} className="text-pink-300" />
                        </button>
                        
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                            {/* Avatar Image */}
                            <div className="w-40 h-40 md:w-52 md:h-52 rounded-full relative animate-glow">
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-800 to-red-900 p-1">
                                    <div className="w-full h-full rounded-full bg-slate-900 overflow-hidden flex items-center justify-center">
                                        <Heart size={48} className="text-red-400 animate-pulse-soft" />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Avatar Details */}
                            <div className="flex-1">
                                <div className="flex items-baseline mb-2">
                                    <h2 className="text-3xl font-bold mr-2">{avatar.name}</h2>
                                    <span className="text-pink-300 text-lg">{avatar.ageRange}</span>
                                </div>
                                
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {avatar.personalityTraits.map((trait, i) => (
                                        <span key={i} className="px-2 py-1 bg-red-900/20 rounded-full text-sm text-pink-300">
                                            {trait}
                                        </span>
                                    ))}
                                </div>
                                
                                <p className="text-pink-300 mb-4">{avatar.description}</p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-slate-900/50 p-3 rounded-lg">
                                        <div className="flex items-center mb-2">
                                            <Calendar size={16} className="text-pink-400 mr-2" />
                                            <span className="text-pink-200 text-sm">Age Group</span>
                                        </div>
                                        <p>{avatar.ageRange}</p>
                                    </div>
                                    
                                    <div className="bg-slate-900/50 p-3 rounded-lg">
                                        <div className="flex items-center mb-2">
                                            <MessageCircle size={16} className="text-pink-400 mr-2" />
                                            <span className="text-pink-200 text-sm">Personality</span>
                                        </div>
                                        <p>{avatar.personalityTraits.join(", ")}</p>
                                    </div>
                                    
                                    <div className="bg-slate-900/50 p-3 rounded-lg">
                                        <div className="flex items-center mb-2">
                                            <Sparkles size={16} className="text-pink-400 mr-2" />
                                            <span className="text-pink-200 text-sm">Interests</span>
                                        </div>
                                        <p>{avatar.interests.join(", ")}</p>
                                    </div>
                                    
                                    <div className="bg-slate-900/50 p-3 rounded-lg">
                                        <div className="flex items-center mb-2">
                                            <Users size={16} className="text-pink-400 mr-2" />
                                            <span className="text-pink-200 text-sm">Compatibility</span>
                                        </div>
                                        <div className="flex items-center">
                                            <div className="h-2 rounded-full bg-slate-800 w-full mr-3">
                                                <div 
                                                    className="h-full rounded-full bg-gradient-to-r from-red-600 to-pink-500" 
                                                    style={{ width: `${avatar.compatibility}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-pink-300 font-bold">{avatar.compatibility}%</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <button 
                                    className="text-base w-full py-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full text-lg font-medium hover:opacity-90 transition-all shadow-lg shadow-red-900/20 flex items-center justify-center"
                                    onClick={() => window.location.href = `/profile/${avatar.id}`}
                                >
                                    <span>View Profile</span>
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-fuchsia-950 to-gray-950 text-white">
            {/* Add animation styles */}
            <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
            
            {/* Background decorations */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-fuchsia-900/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/3 left-1/5 w-72 h-72 bg-pink-900/5 rounded-full blur-3xl"></div>
            </div>            
            <button 
                onClick={toggleDrawer}
                className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-slate-900/50 hover:bg-slate-800/60 text-white transition-colors z-20 hidden max-lg:flex"
            >
                <FiMenu className="w-5 h-5" />
            </button>
            {/* Main Content */}
            <main className="container mx-auto px-4 py-8 relative z-10">

                {/* Hero Section */}
                <div className="text-center mb-10 animate-fade-in">
                    <h1 className="text-4xl md:text-6xl font-bold mb-4">
                        Find Your <span className="shimmer-effect">Perfect Match</span>
                    </h1>
                    <p className="text-pink-200 text-xl max-w-3xl mx-auto">
                        Every connection is a doorway to a deeper relationship... Discover who awaits you
                    </p>
                    
                    {/* Search Bar */}
                    {/* <div className="mt-8 max-w-xl mx-auto relative">
                        <input 
                            type="text" 
                            placeholder="Search by name, personality, or interests..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="text-base w-full px-6 py-3 rounded-full bg-slate-800/60 border border-red-900/20 text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 pr-12 placeholder-pink-300/50 shadow-inner"
                        />
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                            <Search className="h-5 w-5 text-pink-400" />
                        </div>
                    </div> */}
                </div>
                
                {/* Featured Avatar - Only shown if no search or filters are active */}
                {searchQuery === "" && selectedCategory === "all" && selectedAgeRange === "all" && selectedPersonality === "all" && featuredAvatar && (
                    <div className="mb-12 animate-fade-in">
                        <div className="bg-gradient-to-r from-slate-900/90 via-fuchsia-950/80 to-slate-900/90 rounded-xl p-6 border border-red-900/30 shadow-lg animate-border-pulse">
                            <div className="flex flex-col md:flex-row items-center gap-6">
                                <div className="md:w-1/3 flex justify-center">
                                    <div className="w-48 h-48 rounded-full relative animate-glow">
                                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-700 to-red-900 p-1">
                                            <div className="w-full h-full rounded-full bg-slate-900 overflow-hidden flex items-center justify-center">
                                                <Heart size={48} className="text-red-400 animate-pulse-soft" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="md:w-2/3 text-center md:text-left">
                                    <div className="flex items-center justify-center md:justify-start mb-2">
                                        <Sparkles className="h-5 w-5 text-pink-400 mr-2" />
                                        <span className="text-pink-300 uppercase tracking-wider text-sm font-medium">Featured Companion</span>
                                    </div>
                                    
                                    <h2 className="text-3xl font-bold mb-2">{featuredAvatar.name}</h2>
                                    <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
                                        {featuredAvatar.personalityTraits.map((trait, i) => (
                                            <span key={i} className="px-2 py-1 bg-red-900/20 rounded-full text-sm text-pink-300">
                                                {trait}
                                            </span>
                                        ))}
                                    </div>
                                    
                                    <p className="text-pink-200 mb-6">{featuredAvatar.description}</p>
                                    
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <button 
                                            className="text-base px-6 py-3 bg-gradient-to-r from-red-800 via-red-900 to-red-950 rounded-full font-medium flex items-center justify-center shadow-lg shadow-red-900/20 hover:opacity-90 transition-all flex-1"
                                            onClick={() => window.location.href = `/profile/${featuredAvatar.id}`}
                                        >
                                            <span>View Profile</span>
                                            <ArrowRight className="ml-2 h-5 w-5" />
                                        </button>
                                        
                                        <button 
                                            className="text-base px-6 py-3 bg-slate-900/70 border border-red-900/30 rounded-full font-medium hover:bg-slate-800/80 transition-all flex-1"
                                            onClick={() => setDetailsModal(featuredAvatar)}
                                        >
                                            Learn More
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Filter Bar */}
                <div className="bg-slate-900/50 backdrop-blur-md rounded-xl p-4 mb-8 border border-red-900/20 shadow-lg animate-fade-in">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <Filter className="h-5 w-5 text-pink-400 mr-2" />
                            <h2 className="text-lg font-medium text-pink-200">Filters</h2>
                        </div>
                        
                        <button 
                            onClick={() => setShowFilters(!showFilters)}
                            className="text-base text-pink-300 flex items-center text-sm hover:text-white transition-colors"
                        >
                            {showFilters ? "Hide Filters" : "Show All Filters"}
                            <ChevronDown className={`h-4 w-4 ml-1 transform transition-transform ${showFilters ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                    
                    {/* Basic Filters - Always Visible */}
                    <div className="flex flex-wrap gap-3 mt-4">
                        {/* Category Filters */}
                        <div className="flex overflow-x-auto pb-2 gap-2 hide-scrollbar">
                            {categories.map(category => (
                                <button 
                                    key={category.id} 
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`text-base px-4 py-1.5 rounded-full whitespace-nowrap transition-all duration-300 ${
                                        selectedCategory === category.id 
                                            ? "bg-gradient-to-r from-red-800 to-red-900 text-white shadow-md" 
                                            : "bg-slate-800/50 text-pink-200 hover:bg-slate-700/60 hover:text-white border border-red-900/20"
                                    }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    
                    {/* Advanced Filters - Expandable */}
                    {showFilters && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                            {/* Age Ranges */}
                            <div>
                                <h3 className="text-sm text-pink-300 mb-2">Age Range</h3>
                                <div className="flex flex-wrap gap-2">
                                    {ageRanges.map(age => (
                                        <button 
                                            key={age.id} 
                                            onClick={() => setSelectedAgeRange(age.id)}
                                            className={`text-base px-3 py-1 rounded-full text-sm whitespace-nowrap transition-all ${
                                                selectedAgeRange === age.id 
                                                    ? "bg-gradient-to-r from-red-800 to-red-900 text-white shadow-sm" 
                                                    : "bg-slate-800/50 text-pink-200 hover:bg-slate-700/60 hover:text-white border border-red-900/20"
                                            }`}
                                        >
                                            {age.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Personality Types */}
                            <div>
                                <h3 className="text-sm text-pink-300 mb-2">Personality</h3>
                                <div className="flex flex-wrap gap-2">
                                    {personalities.map(personality => (
                                        <button 
                                            key={personality.id} 
                                            onClick={() => setSelectedPersonality(personality.id)}
                                            className={`text-basepx-3 py-1 rounded-full text-sm whitespace-nowrap transition-all ${
                                                selectedPersonality === personality.id 
                                                    ? "bg-gradient-to-r from-red-800 to-red-900 text-white shadow-sm" 
                                                    : "bg-slate-800/50 text-pink-200 hover:bg-slate-700/60 hover:text-white border border-red-900/20"
                                            }`}
                                        >
                                            {personality.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Results Count */}
                <div className="mb-6 flex items-center justify-between">
                    <div className="text-pink-300">
                        <span className="font-medium text-white">{filteredAvatars.length}</span> companions found
                    </div>
                    
                    {/* Clear Filters */}
                    {(selectedCategory !== "all" || selectedAgeRange !== "all" || selectedPersonality !== "all" || searchQuery !== "") && (
                        <button 
                            onClick={() => {
                                setSelectedCategory("all");
                                setSelectedAgeRange("all");
                                setSelectedPersonality("all");
                                setSearchQuery("");
                            }}
                            className="text-base text-pink-300 text-sm hover:text-white transition-colors flex items-center"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Clear Filters
                        </button>
                    )}
                </div>
                
                {/* No Results Message */}
                {filteredAvatars.length === 0 && (
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-8 text-center border border-red-900/20 animate-fade-in">
                        <div className="w-16 h-16 rounded-full bg-slate-800/80 flex items-center justify-center mx-auto mb-4">
                            <Search className="h-6 w-6 text-pink-400" />
                        </div>
                        <h3 className="text-xl font-medium mb-2">No companions found</h3>
                        <p className="text-pink-300 mb-4">Try adjusting your filters or search criteria</p>
                        <button 
                            onClick={() => {
                                setSelectedCategory("all");
                                setSelectedAgeRange("all");
                                setSelectedPersonality("all");
                                setSearchQuery("");
                            }}
                            className="text-base px-4 py-2 bg-slate-800/70 hover:bg-slate-700/80 rounded-full text-sm transition-colors"
                        >
                            Reset Filters
                        </button>
                    </div>
                )}
                
                {/* Avatars Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {filteredAvatars.map((avatar, index) => (
                        <div 
                            key={avatar.id} 
                            className="relative avatar-card animate-fade-in"
                            style={{ animationDelay: `${index * 0.05}s` }}
                            onMouseEnter={() => setHoveredAvatar(avatar.id)}
                            onMouseLeave={() => setHoveredAvatar(null)}
                        >
                            {/* Badge for premium or new avatars */}
                            {(avatar.isPremium || avatar.isNew || avatar.isPopular) && (
                                <div className="absolute top-2 right-2 z-10">
                                    {avatar.isPremium && (
                                        <div className="px-2 py-1 bg-gradient-to-r from-red-800 to-red-900 rounded-full text-xs font-medium shadow-lg shadow-red-900/20 flex items-center mb-1">
                                            <Lock size={10} className="mr-1" />
                                            Premium
                                        </div>
                                    )}
                                    {avatar.isNew && !avatar.isPremium && (
                                        <div className="px-2 py-1 bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-full text-xs font-medium mb-1">
                                            New
                                        </div>
                                    )}
                                    {avatar.isPopular && !avatar.isPremium && !avatar.isNew && (
                                        <div className="px-2 py-1 bg-gradient-to-r from-amber-700 to-amber-800 rounded-full text-xs font-medium mb-1">
                                            Popular
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Glowing effect when hovered */}
                            <div 
                                className={`absolute inset-0 bg-gradient-to-br from-red-700/20 to-red-900/20 rounded-xl blur-md transition-all duration-500 -z-10 ${
                                    hoveredAvatar === avatar.id ? 'opacity-100 scale-105' : 'opacity-0 scale-100'
                                }`}
                            ></div>
                            
                            <div className="bg-slate-900/50 backdrop-blur-md rounded-xl overflow-hidden border border-red-900/20 h-full transition-all duration-300 hover:shadow-lg hover:shadow-red-900/20 relative">
                                {/* Avatar Image with Circle Frame */}
                                <div className="aspect-square relative overflow-hidden bg-gradient-to-b from-slate-900 to-gray-950 flex items-center justify-center">
                                    <div className={`w-36 h-36 rounded-full relative transition-all duration-500 ${
                                        hoveredAvatar === avatar.id ? 'scale-105' : 'scale-100'
                                    }`}>
                                        {/* Glowing border */}
                                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-800 to-red-900 p-1">
                                            {/* Avatar placeholder */}
                                            <div className="w-full h-full rounded-full bg-slate-900 overflow-hidden flex items-center justify-center">
                                                {/* Heart icon in center */}
                                                <Heart size={32} className="text-red-400 animate-pulse-soft" />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Compatibility indicator */}
                                    {/* <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                        <Star size={12} className="text-pink-400 mr-1" />
                                        <span>{avatar.compatibility}% Match</span>
                                    </div> */}
                                </div>
                                
                                {/* Avatar Info */}
                                <div className="p-5 text-center">
                                    <h3 className="text-xl font-light mb-1 text-pink-200">Meet</h3>
                                    <h2 className="text-2xl font-bold mb-2">{avatar.name}</h2>
                                    
                                    <div className="flex flex-wrap gap-1 justify-center mb-3">
                                        {avatar.personalityTraits.slice(0, 2).map((trait, i) => (
                                            <span key={i} className="px-2 py-0.5 bg-red-900/20 rounded-full text-xs text-pink-300">
                                                {trait}
                                            </span>
                                        ))}
                                    </div>
                                    
                                    <p className="text-pink-200/70 text-sm mb-5 line-clamp-2">
                                        {avatar.description}
                                    </p>
                                    
                                    <div className="flex gap-2">
                                        <button 
                                            className="text-base flex-1 px-4 py-2 rounded-full bg-gradient-to-r from-red-800 to-red-900 text-white shadow-md hover:opacity-90 transition-all"
                                            onClick={() => window.location.href = `/profile/${avatar.id}`}
                                        >
                                            Profile
                                        </button>
                                        
                                        <button 
                                            className="text-base flex-1 px-4 py-2 rounded-full bg-slate-800/70 border border-red-900/30 text-pink-200 hover:bg-slate-700/80 transition-all"
                                            onClick={() => setDetailsModal(avatar)}
                                        >
                                            Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Avatar Details Modal */}
                <AvatarDetailsModal 
                    avatar={detailsModal} 
                    onClose={() => setDetailsModal(null)} 
                />
            </main>
        </div>
    );
};

export default DiscoverComponent;